import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, Loader2, MessageSquarePlus, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { supabase } from "@/lib/supabaseClient";
import {
  normalizeSupportCategory,
  supportCategoryLabel,
  supportStatusLabel,
} from "@/lib/supportAccess";
import {
  createSupportConversation,
  getSupportConversation,
  listSupportConversations,
  resolveSupportConversation,
  sendSupportConversationMessage,
  uploadSupportAttachment,
} from "@/lib/supportApi";
import SupportComposer from "@/components/support/SupportComposer";
import SupportThread from "@/components/support/SupportThread";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
];

const STATUS_DOT = {
  open: "bg-blue-500",
  in_progress: "bg-amber-500",
  resolved: "bg-slate-400",
};

export default function Support() {
  const { activeClinic } = useAuth();
  const { moduleLoading, canAccessModule } = useFeatures();
  const allowed = !moduleLoading && canAccessModule("support");
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState("");
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setLoadingList(true);
    listSupportConversations()
      .then((rows) => {
        if (cancelled) return;
        setConversations(rows);
        if (!selectedId && rows[0]?.id) setSelectedId(rows[0].id);
      })
      .catch((loadError) => setError(loadError?.message || "Could not load support tickets."))
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => { cancelled = true; };
  }, [allowed, selectedId]);

  useEffect(() => {
    if (!allowed || !selectedId) {
      setConversation(null);
      setMessages([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingThread(true);
    getSupportConversation(selectedId)
      .then((payload) => {
        if (cancelled) return;
        setConversation(payload.conversation);
        setMessages(payload.messages);
      })
      .catch((loadError) => setError(loadError?.message || "Could not load this ticket."))
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    return () => { cancelled = true; };
  }, [allowed, selectedId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const channel = supabase
      .channel(`support-page-${selectedId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: `conversation_id=eq.${selectedId}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setMessages((current) => (
            current.some((existing) => existing.id === row.id) ? current : [...current, row]
          ));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedId]);

  useEffect(() => {
    const node = threadRef.current;
    if (!node) return;
    if (typeof node.scrollTo === "function") {
      node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
    } else {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, selectedId]);

  const visibleConversations = useMemo(() => {
    const rows = filter === "all"
      ? conversations
      : conversations.filter((row) => row.status === filter);
    return [...rows].sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at));
  }, [conversations, filter]);

  if (moduleLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Checking clinic permissions...
      </div>
    );
  }
  if (!allowed) return <Navigate to="/dashboard" replace />;

  async function handleAttach(file) {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadSupportAttachment({
        file,
        clinicId: activeClinic?.clinic_id,
        conversationId: selectedId || "new-ticket",
      });
      setAttachment(uploaded);
    } catch (attachError) {
      setError(attachError?.message || "Could not upload attachment.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSend(body) {
    if (!selectedId || (!body && !attachment)) return;
    setSending(true);
    setError("");
    try {
      const payload = await sendSupportConversationMessage({
        conversationId: selectedId,
        body,
        attachment,
      });
      setConversation(payload.conversation);
      setMessages(payload.messages);
      setAttachment(null);
      const refreshed = await listSupportConversations();
      setConversations(refreshed);
    } catch (sendError) {
      setError(sendError?.message || "Could not send this right now.");
      toast.error("Could not send support message.");
    } finally {
      setSending(false);
    }
  }

  async function handleResolve() {
    if (!selectedId) return;
    setSending(true);
    try {
      const updated = await resolveSupportConversation(selectedId);
      setConversation(updated?.conversation || { ...conversation, status: "resolved" });
      const refreshed = await listSupportConversations();
      setConversations(refreshed);
    } catch (resolveError) {
      setError(resolveError?.message || "Could not mark this resolved.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] min-h-[620px] max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <aside className="flex w-[360px] shrink-0 flex-col border-r border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-950">Support tickets</h1>
            <p className="text-xs text-slate-500">{loadingList ? "Loading..." : `${conversations.length} total`}</p>
          </div>
          <button
            type="button"
            onClick={() => setNewTicketOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-700 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-800"
          >
            <MessageSquarePlus className="h-4 w-4" />
            New ticket
          </button>
        </div>
        <div className="flex gap-2 border-b border-slate-200 bg-white px-4 py-3">
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={[
                "rounded-lg px-3 py-1.5 text-sm font-medium",
                filter === item.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {visibleConversations.length === 0 ? (
            <div className="px-6 py-10 text-sm text-slate-500">
              No tickets yet. Send us a message or open a ticket.
            </div>
          ) : visibleConversations.map((ticket) => (
            <button
              key={ticket.id}
              type="button"
              onClick={() => setSelectedId(ticket.id)}
              className={[
                "w-full border-b border-slate-200 px-5 py-4 text-left hover:bg-white",
                selectedId === ticket.id ? "border-l-4 border-l-teal-600 bg-white" : "border-l-4 border-l-transparent",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className={ticket.unread_count > 0 ? "font-semibold text-slate-950" : "font-medium text-slate-900"}>
                  {ticket.subject}
                </div>
                <div className="shrink-0 text-xs text-slate-500">{formatRelative(ticket.last_message_at || ticket.created_at)}</div>
              </div>
              <p className="mt-1 truncate text-sm text-slate-500">{ticket.last_message_preview || "No messages yet"}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${STATUS_DOT[ticket.status] || "bg-slate-400"}`} />
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700">
                  {supportCategoryLabel(ticket.category)}
                </span>
                {ticket.priority === "urgent" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                    <AlertCircle className="h-3 w-3" />
                    Urgent
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        {conversation ? (
          <>
            <header className="border-b border-slate-200 bg-white px-6 py-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">{conversation.subject}</h2>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                      {supportStatusLabel(conversation.status)}
                    </span>
                    {conversation.priority === "urgent" ? (
                      <span className="rounded-full bg-red-50 px-2.5 py-1 font-semibold text-red-700">Urgent</span>
                    ) : null}
                    <span>{supportCategoryLabel(conversation.category)}</span>
                    <span>Opened {formatDate(conversation.created_at)}</span>
                  </div>
                </div>
                {conversation.status !== "resolved" ? (
                  <button
                    type="button"
                    onClick={handleResolve}
                    disabled={sending}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Mark resolved
                  </button>
                ) : null}
              </div>
            </header>
            <div ref={threadRef} className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
              {loadingThread ? (
                <div className="px-6 py-10 text-center text-sm text-slate-500">Loading thread...</div>
              ) : (
                <SupportThread messages={messages} />
              )}
            </div>
            {conversation.status === "resolved" ? (
              <div className="border-t border-slate-200 bg-white px-6 py-4 text-sm text-slate-600">
                This conversation has been resolved. Send a message to reopen.
                <SupportComposer
                  placeholder="Reply to reopen..."
                  sending={sending}
                  uploading={uploading}
                  error={error}
                  attachment={attachment}
                  onAttach={handleAttach}
                  onClearAttachment={() => setAttachment(null)}
                  onSend={handleSend}
                />
              </div>
            ) : (
              <SupportComposer
                sending={sending}
                uploading={uploading}
                error={error}
                attachment={attachment}
                onAttach={handleAttach}
                onClearAttachment={() => setAttachment(null)}
                onSend={handleSend}
              />
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center px-8 text-center text-sm text-slate-500">
            No tickets yet. Send us a message or open a ticket.
          </div>
        )}
      </section>

      {newTicketOpen ? (
        <NewTicketModal
          clinicId={activeClinic?.clinic_id}
          onClose={() => setNewTicketOpen(false)}
          onCreated={(payload) => {
            setNewTicketOpen(false);
            setSelectedId(payload.conversation?.id);
            setConversation(payload.conversation);
            setMessages(payload.messages);
            listSupportConversations().then(setConversations).catch(() => {});
          }}
        />
      ) : null}
    </div>
  );
}

function NewTicketModal({ clinicId, onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      setAttachment(await uploadSupportAttachment({ file, clinicId, conversationId: "new-ticket" }));
    } catch (uploadError) {
      setError(uploadError?.message || "Could not upload attachment.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = await createSupportConversation({
        subject,
        category: normalizeSupportCategory(category),
        body: description,
        attachment,
      });
      onCreated(payload);
    } catch (submitError) {
      setError(submitError?.message || "Could not create ticket.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
      <form onSubmit={handleSubmit} className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-950">New ticket</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close new ticket">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <label className="block text-sm font-medium text-slate-700">
            Subject
            <input
              required
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Category
            <select
              required
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            >
              <option value="bug">Bug</option>
              <option value="billing">Billing</option>
              <option value="feature_request">Feature request</option>
              <option value="general">General</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Description
            <textarea
              required
              rows={5}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="mt-1 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            />
          </label>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(event) => handleFile(event.target.files?.[0] || null)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || busy}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Paperclip className="h-4 w-4" />
            {attachment ? attachment.name : uploading ? "Uploading..." : "Attach file"}
          </button>
          {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy || uploading} className="rounded-xl bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50">
            {busy ? "Creating..." : "Create ticket"}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function formatRelative(value) {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const hours = Math.floor(diffMs / 36e5);
  if (hours < 1) return "Now";
  if (hours < 24) return `${hours}h ago`;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}
